import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { refreshAdminNotifications } from "@/components/NotificationBell";

export const EDIT_REASONS = [
  "고객 요청으로 연락처 정정",
  "상담 내용 입력 오류",
  "견적 관련 내용 오기재",
  "담당자 입력 실수",
  "기타",
];

export const EDIT_STATUS_LABEL = {
  PENDING: "수정 승인 대기",
  APPROVED: "수정 승인됨",
  REJECTED: "수정 요청 거절",
  USED: "수정 완료",
  EXPIRED: "수정 승인 만료",
};

export function editBadge(lock) {
  const st = lock?.request?.status || lock?.active_request?.status;
  if (!lock?.locked) return null;
  if (st && EDIT_STATUS_LABEL[st]) return EDIT_STATUS_LABEL[st];
  return "완료";
}

export function ConsultationEditBanner({
  lock,
  isOwner,
  onRequest,
  onStartEdit,
  onOpenAudit,
}) {
  if (!lock?.locked) return null;
  const req = lock.request || lock.active_request;
  const st = req?.status;
  return (
    <div className="fc-lock-banner" data-testid="consult-edit-lock">
      {st === "PENDING" && (
        <p>수정 승인 요청이 진행 중입니다.{req?.requested_by_name ? ` 요청자: ${req.requested_by_name}` : ""}{req?.requested_at ? ` · ${String(req.requested_at).slice(0, 16).replace("T", " ")}` : ""}</p>
      )}
      {st === "APPROVED" && !isOwner && (
        <p>수정 승인이 완료되었습니다. 저장하면 다시 잠깁니다.{req?.expires_at ? ` (유효: ${String(req.expires_at).slice(0, 16).replace("T", " ")})` : ""}</p>
      )}
      {st === "REJECTED" && (
        <p>수정 요청이 승인되지 않았습니다.{req?.review_note ? ` 사유: ${req.review_note}` : ""}</p>
      )}
      {st === "USED" && <p>이 기록은 수정된 기록입니다. 추가 수정은 다시 승인이 필요합니다.</p>}
      {st === "EXPIRED" && <p>수정 승인 유효시간이 만료되었습니다. 다시 요청해 주세요.</p>}
      {(!st || st === "REJECTED" || st === "USED" || st === "EXPIRED") && !isOwner && (
        <p>완료된 상담기록은 수정이 제한됩니다. 수정이 필요한 경우 슈퍼관리자 승인이 필요합니다.</p>
      )}
      {isOwner && (!st || st === "USED" || st === "REJECTED" || st === "EXPIRED") && (
        <p>완료된 상담기록입니다. 최고관리자는 바로 수정할 수 있습니다.</p>
      )}
      <div className="fc-lock-actions">
        {!isOwner && lock.can_request ? (
          <button type="button" className="fc-btn" data-testid="consult-edit-request" onClick={onRequest}>수정 권한 요청</button>
        ) : null}
        {(isOwner || st === "APPROVED") && lock.can_edit ? (
          <button type="button" className="fc-btn" data-testid="consult-edit-start" onClick={onStartEdit}>수정</button>
        ) : null}
        {isOwner && lock.has_audits ? (
          <button type="button" className="fc-btn ghost" data-testid="consult-edit-audit" onClick={onOpenAudit}>수정 이력</button>
        ) : null}
        {!isOwner && lock.modified ? <span className="fc-lock-note">수정된 기록입니다</span> : null}
      </div>
    </div>
  );
}

export function EditRequestModal({ onClose, onSubmit }) {
  const [reason, setReason] = useState(EDIT_REASONS[0]);
  const [detail, setDetail] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (reason === "기타" && !detail.trim()) {
      toast.error("기타 사유를 입력해 주세요.");
      return;
    }
    setSaving(true);
    try {
      await onSubmit({ reason, reason_detail: detail });
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="consult-edit-request-modal">
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 w-full max-w-md space-y-3">
        <h4 className="font-medium">수정 권한 요청</h4>
        <p className="text-sm text-white/60">슈퍼관리자 승인 전까지 이 기록은 수정되지 않습니다.</p>
        <label className="block text-xs text-white/45">수정 사유 *</label>
        <select className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="consult-edit-reason">
          {EDIT_REASONS.map((r) => <option key={r}>{r}</option>)}
        </select>
        <label className="block text-xs text-white/45">수정하려는 항목 / 설명</label>
        <textarea className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" rows={3} value={detail} onChange={(e) => setDetail(e.target.value)} data-testid="consult-edit-reason-detail" />
        <div className="flex gap-2">
          <button type="button" className="border border-white/10 rounded-lg px-3 py-2 text-sm" onClick={onClose}>취소</button>
          <button type="button" className="bg-white text-black rounded-lg px-3 py-2 text-sm disabled:opacity-50" disabled={saving} onClick={submit} data-testid="consult-edit-request-submit">
            {saving ? "요청 중..." : "승인 요청"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function PendingEditQueue({ items, onOpen, onApprove, onReject }) {
  if (!items?.length) return null;
  return (
    <div className="fc-pending-edits" data-testid="consult-edit-pending-queue">
      <div className="fc-pending-edits__title">상담기록 수정 승인 대기</div>
      {items.map((row) => (
        <div key={row.id} className="fc-pending-edits__row" data-testid={`consult-edit-pending-${row.id}`}>
          <button type="button" onClick={() => onOpen(row)}>
            <div>{row.company_name || "상담기록"}</div>
            <div className="meta">{row.requested_by_name || "-"} · {row.reason} · {String(row.requested_at || "").slice(0, 16).replace("T", " ")}</div>
          </button>
          <div className="fc-list-actions">
            <button type="button" className="fc-btn" onClick={() => onApprove(row)} data-testid={`consult-edit-approve-${row.id}`}>승인</button>
            <button type="button" className="fc-archive" onClick={() => onReject(row)} data-testid={`consult-edit-reject-${row.id}`}>거절</button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function RejectEditModal({ onClose, onSubmit }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="consult-edit-reject-modal">
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 w-full max-w-md space-y-3">
        <h4 className="font-medium">수정 요청 거절</h4>
        <textarea className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" rows={3} placeholder="거절 사유 (선택)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="consult-edit-reject-note" />
        <div className="flex gap-2">
          <button type="button" className="border border-white/10 rounded-lg px-3 py-2 text-sm" onClick={onClose}>취소</button>
          <button
            type="button"
            className="bg-red-500 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50"
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try { await onSubmit(note); } finally { setSaving(false); }
            }}
            data-testid="consult-edit-reject-submit"
          >
            거절
          </button>
        </div>
      </div>
    </div>
  );
}

export function AuditDrawer({ items, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="consult-edit-audit-modal">
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 w-full max-w-lg max-h-[80vh] overflow-auto space-y-3">
        <div className="flex justify-between gap-3">
          <h4 className="font-medium">수정 이력</h4>
          <button type="button" className="text-sm text-white/50" onClick={onClose}>닫기</button>
        </div>
        {!items?.length && <p className="text-sm text-white/50">이력이 없습니다.</p>}
        {items.map((row) => (
          <article key={row.id} className="border border-white/10 rounded-xl p-3 text-sm">
            <div className="text-white/80">{row.edited_by_name || row.edited_by_email} · {String(row.created_at || "").slice(0, 19).replace("T", " ")}</div>
            <div className="text-white/45 text-xs mt-1">승인자 {row.approved_by_name || "-"} · 사유 {row.reason || "-"}</div>
            <ul className="mt-2 space-y-1 text-xs text-white/70">
              {(row.changes || []).slice(0, 40).map((ch) => (
                <li key={ch.field}><b>{ch.field}</b>: {formatVal(ch.before)} → {formatVal(ch.after)}</li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </div>
  );
}

function formatVal(v) {
  if (v == null || v === "") return "(없음)";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export async function submitEditRequest(cid, payload) {
  const r = await api.post(`/admin/consultations/${cid}/edit-requests`, payload);
  refreshAdminNotifications();
  return r.data;
}

export async function decideEditRequest(id, ok, reviewNote = "") {
  await api.post(`/admin/consultation-edit-requests/${id}/${ok ? "approve" : "reject"}`, ok ? {} : { review_note: reviewNote });
  refreshAdminNotifications();
}
